import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { styled } from '@mui/material/styles'
import { useTablePageParam } from '../../hooks/use-table-page-param.hook'
import {
  useTableSortParam,
  SITE_SORT_KEY,
  SITE_SORTABLE_FIELDS,
  SITE_UNSORTABLE_FIELDS
} from '../../hooks/use-table-sort-param.hook'
import { ReactComponent as DataSet } from '@mike/mike-shared-frontend/media/icons/DataSet'
import { ReactComponent as Folder } from '@mike/mike-shared-frontend/media/icons/Folder'
import { css } from '@emotion/css'
import capitalize from 'lodash/capitalize'
import AppNav from '../AppNav'
import { useTypedSelector } from '../../reducers'
import SearchInput from '../SearchInput'
import { useIntl } from 'react-intl'
import RefreshButton from '../buttons/RefreshButton'
import CreateSubprojectButton from '../buttons/CreateSubprojectButton'
import { StateEnum } from '../../model/StateEnum'
import { useDispatch } from 'react-redux'
import { loadRootProjectList } from '../../actions/rootProjectActions'
import DataTable from '../DataTable'
import messages from '../../shared/messages'
import LongText from '../DataTable/renders/LongText'
import LongName from '../DataTable/renders/LongName'
import DateTime from '../DataTable/renders/DateTime'
import DataTableRenders, { actionStyle, iconStyle } from '../DataTable/renders'
import MikeSlidingPanel from '../SlidingPanel'
import CreateProjectForm from '../CreateProjectForm'
import { loadCustomerUsers } from '../../actions/customerUsers'
import {
  loadProjectContent,
  loadProjectMembers,
  resetProjectContent,
  setProjectPath,
  updateProjectDetails
} from '../../actions/project'
import { createProject, deleteProjects } from '../../actions/projects'
import { ReactComponent as Delete } from '@mike/mike-shared-frontend/media/icons/Delete'
import IProject from '../../model/IProject'
import ConfirmationDialog from '../Dialog/ConfirmationDialog'
import DeleteButton from '../buttons/DeleteButton'
import { getCapabilities } from '../../apis/project'
import { resetRootProjectLoadingState } from '../../reducers/rootProjectList'
import { enhanceProjectMembers } from '../../helpers/project'
import { ReactComponent as Edit } from '@mike/mike-shared-frontend/media/icons/Edit'
import { ReactComponent as Users } from '@mike/mike-shared-frontend/media/icons/Users'
import FolderAccessDialog from '../FolderAccessDialog'
import AppBreadcrumbNav from '../AppBreadcrumpsNav'
import { ICrumb, trailState } from '../../helpers/breadcrumbTrail'

const StyledTableTopHeader = styled('div')(({ theme }) => ({
  zIndex: 1,
  backgroundColor: '#f2f5f7',
  paddingTop: theme.spacing(3)
}))

const StyledProjectDetails = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'start',
  zIndex: 1,
  paddingLeft: '30px',
  paddingRight: theme.spacing(2),
  paddingBottom: '16px'
}))

const StyledTableTopActionButtons = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  paddingBottom: theme.spacing(3),
  paddingLeft: '30px',
  paddingRight: '30px'
}))

const StyledLeftActionButtons = styled('div')({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'flex-end'
})

const StyledRightActionButtons = styled('div')({
  display: 'flex',
  justifyContent: 'center'
})

const StyledCreateSubprojectButton = styled('div')({
  marginLeft: 20
})

const formatRender = value => {
  return value ? capitalize(value) : 'Folder'
}

const longTextRender = value => <LongText longText={value} />

const itemNameRender = (value, item) => (
  <LongName longName={value} icon={'datasetType' in item ? DataSet : Folder} />
)

const dateTimeRender = value => <DateTime jsonTime={value} />

const hoverStyle = item => {
  return 'datasetType' in item
    ? null
    : css`
        &:hover {
          cursor: pointer;
        }
      `
}

const RootProject = () => {
  const intl = useIntl()
  const dispatch = useDispatch()

  const {
    state: rootProjectState,
    params: queryParams,
    projects,
    totalCount
  } = useTypedSelector(state => state['rootProjectList'])

  const [urlPage, setUrlPage] = useTablePageParam()
  const [savedRowsPerPage] = useState<number>(() => {
    const raw = localStorage.getItem('site-rowsPerPage')
    return raw
      ? parseInt(raw, 10) || queryParams.rowsPerPage
      : queryParams.rowsPerPage
  })

  const [savedSort, persistSort] = useTableSortParam(
    SITE_SORT_KEY,
    {
      orderBy: queryParams.orderBy,
      sortOrder: queryParams.sortOrder
    },
    SITE_SORTABLE_FIELDS
  )

  const tenantId =
    useTypedSelector(state => state['auth']?.user?.tenantId) || ''
  const user = useTypedSelector(state => state['auth'].user)
  const customerUsers = useTypedSelector(state => state['customerUsers'].data)
  const loadingCustomerUsers = useTypedSelector(
    state => state['customerUsers'].loading
  )
  const loadingProjectMembers = useTypedSelector(
    state => state['project'].members.loading
  )
  const projectMembers = useTypedSelector(
    state => state['project'].members.data
  )
  const [selectedItems, setSelectedItems] = useState<IProject[]>([])
  const [createProjectOpen, setCreateProjectOpen] = useState(false)
  const [editProjectOpen, setEditProjectOpen] = useState(false)
  const [editingProject, setEditingProject] = useState<IProject | null>(null)
  const [deleteItems, setDeleteItems] = useState<IProject[]>([])
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [capabilities, setCapabilities] = useState({})

  const [manageAccessPanel, setManageAccessPanel] = useState<{
    open: boolean
    projectId: string | null
  }>({
    open: false,
    projectId: null
  })

  const [searchText, setSearchText] = useState('')
  const canCreateContent =
    useTypedSelector(state => state['admin']?.isRootContributor) || false

  const isAdmin = useTypedSelector(state => state['admin']?.isAdmin) || false

  useEffect(() => {
    if (tenantId) {
      dispatch(loadCustomerUsers(tenantId))

      if (isAdmin) {
        dispatch(loadProjectMembers(tenantId))
      }
    }
  }, [dispatch, isAdmin, tenantId])

  useEffect(() => {
    return () => {
      dispatch(resetRootProjectLoadingState())
    }
  }, [dispatch])

  useEffect(() => {
    dispatch(resetProjectContent())
    dispatch(setProjectPath(null))
  }, [dispatch])

  useEffect(() => {
    if (rootProjectState === StateEnum.INIT) {
      dispatch(
        loadRootProjectList({
          ...queryParams,
          page: urlPage,
          rowsPerPage: savedRowsPerPage,
          orderBy: savedSort.orderBy,
          sortOrder: savedSort.sortOrder,
          namePrefix: searchText
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootProjectState, dispatch, searchText])

  useEffect(() => {
    if (totalCount >= 0 && queryParams.page > 0) {
      const totalPages = Math.ceil(totalCount / queryParams.rowsPerPage)
      if (queryParams.page >= totalPages) {
        onChangePage(0)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount])

  const loading =
    rootProjectState === StateEnum.LOADING ||
    rootProjectState === StateEnum.INIT

  const onSearchTextChange = text => {
    setSearchText(text)
    dispatch(loadRootProjectList({ ...queryParams, namePrefix: text }))
  }

  const onRefreshClick = () => {
    dispatch(loadRootProjectList(queryParams))
  }

  const onCreateProjectOpenClick = () => {
    setCreateProjectOpen(true)
  }

  const [openingProject, setOpeningProject] = useState<ICrumb | null>(null)
  const loadingProjectContent = useTypedSelector(
    state => state['project'].subprojects.loading
  )
  const loadedProjectContentId = useTypedSelector(
    state => state['project'].projectContent.projectId
  )

  const navigate = useNavigate()
  const onColumnClick = useCallback(
    item => {
      setOpeningProject({ id: item.id, name: item.name })
      dispatch(loadProjectContent(item.id))
    },
    [dispatch]
  )

  useEffect(() => {
    if (!openingProject || loadingProjectContent) {
      return
    }
    if (loadedProjectContentId === openingProject.id) {
      navigate(`/project/${openingProject.id}`, {
        state: trailState([openingProject])
      })
    }
    setOpeningProject(null)
  }, [openingProject, loadingProjectContent, loadedProjectContentId, navigate])

  const onHandleRequestSort = (orderBy, sortOrder) => {
    persistSort(orderBy, sortOrder)
    dispatch(
      loadRootProjectList({
        ...queryParams,
        page: 0,
        orderBy,
        sortOrder
      })
    )
  }

  const onChangePage = page => {
    setUrlPage(page)
    dispatch(
      loadRootProjectList({
        ...queryParams,
        page
      })
    )
  }

  const onChangeRowsPerPage = rowsPerPage => {
    localStorage.setItem('site-rowsPerPage', String(rowsPerPage))
    setUrlPage(0)
    dispatch(
      loadRootProjectList({
        ...queryParams,
        page: 0,
        rowsPerPage
      })
    )
  }

  const onCreateProjectCloseClick = () => {
    setCreateProjectOpen(false)
  }

  const onCreateProject = data => {
    data.members = data.members.filter(member => {
      return member.userId !== user?.id
    })
    dispatch(
      createProject(data, () => {
        dispatch(loadRootProjectList(queryParams))
      })
    )
    setCreateProjectOpen(false)
  }

  const onUpdateProject = data => {
    if (editingProject) {
      // `members` is omitted when the member field is hidden (membership is
      // managed by the access panel); only normalize it when present.
      if (data.members) {
        data.members = data.members
          .filter(member => (member.userId || member.id) !== user?.id)
          .map(member => ({
            ...member,
            userId: member.userId || member.id
          }))
      }

      const updateData = {
        ...data,
        id: editingProject.id
      }
      dispatch(
        updateProjectDetails(updateData, () => {
          dispatch(loadRootProjectList(queryParams))
          setEditProjectOpen(false)
          dispatch(loadProjectMembers(editingProject.id))
          setEditingProject(null)
        })
      )
    }
  }

  const canDelete = id => {
    return capabilities[id]?.canDelete || false
  }

  const canEdit = id => {
    const cap = capabilities[id]
    return cap && (cap.canEdit || cap.canEditAccessLevel || cap.canGrantAccess)
  }

  const canInvite = id => {
    return capabilities[id]?.canGrantAccess || false
  }

  const renderDeleteItem = row => {
    const canDel = canDelete(row.id)
    return (
      <div className={actionStyle(!canDel)}>
        <Delete className={iconStyle(!canDel)} />
        Delete
      </div>
    )
  }

  const renderEditItem = row => {
    const canEditFolder = canEdit(row.id)
    return (
      <div
        className={actionStyle(!canEditFolder)}
        style={{ minWidth: '140px' }}
      >
        <Edit className={iconStyle(!canEditFolder)} />
        Edit Folder
      </div>
    )
  }

  const renderManageAccess = row => {
    const canAccess = canInvite(row.id)
    return (
      <div className={actionStyle(!canAccess)} style={{ minWidth: '140px' }}>
        <Users className={iconStyle(!canAccess)} />
        Folder Access
      </div>
    )
  }

  const deleteActionClicked = (row: IProject) => {
    if (canDelete(row.id)) {
      setDeleteItems([row])
      setShowDeleteConfirmation(true)
    }
  }

  const editActionClicked = (row: IProject) => {
    if (canEdit(row.id)) {
      setEditingProject(row)
      setEditProjectOpen(true)
      dispatch(loadProjectMembers(row.id))
    }
  }

  const manageAccessActionClicked = (row: IProject) => {
    // The dialog loads members and resolves API-account names itself; we only
    // gate on the row's grant-access capability and hand it the folder id.
    if (canInvite(row.id)) {
      setManageAccessPanel({ open: true, projectId: row.id })
    }
  }

  const onOkDeleteProjects = () => {
    setShowDeleteConfirmation(false)
    const ids = deleteItems.map(item => item.id).filter(id => canDelete(id))

    dispatch(
      deleteProjects(ids, () => {
        dispatch(loadRootProjectList(queryParams))
        setSelectedItems(selectedItems.filter(item => !ids.includes(item.id)))
      })
    )
  }

  const onCancelDeleteProjects = () => {
    setShowDeleteConfirmation(false)
  }

  const onDeleteItems = () => {
    setDeleteItems(selectedItems)
    setShowDeleteConfirmation(true)
  }

  const fetchCapabilitiesIfNeeded = useCallback(
    id => {
      if (capabilities[id] === null || capabilities[id] === undefined) {
        getCapabilities(id).then(newCap => {
          setCapabilities(prev => ({ ...prev, [id]: newCap }))
        })
      }
    },
    [capabilities]
  )

  const actionPopupStateChange = (open, row) => {
    if (open) {
      fetchCapabilitiesIfNeeded(row.id)
    }
  }

  const onSelectionChange = items => {
    items.forEach(item => {
      fetchCapabilitiesIfNeeded(item.id)
    })
    setSelectedItems(items)
  }

  useEffect(() => {
    projects.forEach(project => {
      fetchCapabilitiesIfNeeded(project.id)
    })
  }, [projects, fetchCapabilitiesIfNeeded])

  const projectsWithCapabilities = useMemo(() => {
    return projects.map(project => ({
      ...project,
      capabilities: capabilities[project.id] || null
    }))
  }, [projects, capabilities])

  const editableProjectWithMembers = useMemo(() => {
    if (!editingProject) return null
    const members = loadingProjectMembers
      ? []
      : enhanceProjectMembers(projectMembers, customerUsers)
    return {
      ...editingProject,
      members
    }
  }, [editingProject, projectMembers, customerUsers, loadingProjectMembers])
  return (
    <>
      <ConfirmationDialog
        open={showDeleteConfirmation}
        title={intl.formatMessage({
          id: 'screens.projects.confirmationDialog.title'
        })}
        message={intl.formatMessage(
          deleteItems.length > 1
            ? { id: 'screens.projects.confirmationDialog.message.multipleProj' }
            : { id: 'screens.projects.confirmationDialog.message.singleProj' }
        )}
        ok={intl.formatMessage(messages.yesDelete)}
        onOk={onOkDeleteProjects}
        onCancel={onCancelDeleteProjects}
      />
      <AppNav tenantId={tenantId} />
      <StyledTableTopHeader>
        <StyledProjectDetails>
          <AppBreadcrumbNav />
        </StyledProjectDetails>

        <StyledTableTopActionButtons>
          <StyledLeftActionButtons>
            <SearchInput
              id="project-details-search-input"
              placeholder={intl.formatMessage({
                id: 'projectDetails.input.search.placeholder'
              })}
              autoFocus
              text={searchText}
              onTextChange={onSearchTextChange}
            />
            <RefreshButton onClick={onRefreshClick} />

            {selectedItems.length > 0 && (
              <DeleteButton disabled={!canDelete} onClick={onDeleteItems} />
            )}
          </StyledLeftActionButtons>
          <StyledRightActionButtons>
            <StyledCreateSubprojectButton>
              {canCreateContent && (
                <CreateSubprojectButton onClick={onCreateProjectOpenClick} />
              )}
            </StyledCreateSubprojectButton>
          </StyledRightActionButtons>
        </StyledTableTopActionButtons>

        <DataTable
          actionsPopupStateChange={actionPopupStateChange}
          actions={[
            {
              name: 'Edit Folder',
              callBack: editActionClicked,
              render: renderEditItem,
              disableGutters: true
            },
            {
              name: 'Folder Access',
              callBack: manageAccessActionClicked,
              render: renderManageAccess,
              disableGutters: true
            },
            {
              name: 'Delete',
              callBack: deleteActionClicked,
              render: renderDeleteItem,
              disableGutters: true
            }
          ]}
          loading={loading || Boolean(openingProject)}
          notSortableColumns={SITE_UNSORTABLE_FIELDS}
          columns={[
            {
              field: 'name',
              label: intl.formatMessage(messages.name),
              render: itemNameRender,
              className: hoverStyle
            },
            {
              field: 'description',
              label: intl.formatMessage(messages.description),
              render: longTextRender,
              className: hoverStyle
            },
            {
              field: 'datasetType',
              label: intl.formatMessage(messages.type),
              render: formatRender,
              className: hoverStyle
            },
            {
              field: 'datasetFormat',
              label: intl.formatMessage(messages.format),
              render: formatRender,
              className: hoverStyle
            },
            {
              field: 'storageSize',
              label: intl.formatMessage(messages.size),
              render: DataTableRenders.size(1024),
              className: hoverStyle
            },
            {
              field: 'createdAt',
              label: intl.formatMessage(messages.createdAt),
              render: dateTimeRender,
              className: hoverStyle
            },
            {
              field: 'updatedAt',
              label: intl.formatMessage(messages.editedAt),
              render: dateTimeRender,
              className: hoverStyle
            }
          ]}
          idField="id"
          data={projectsWithCapabilities}
          selectedRows={selectedItems}
          onColumnClick={onColumnClick}
          onSelectionChange={onSelectionChange}
          onHandleRequestSort={onHandleRequestSort}
          page={queryParams.page}
          _rowsPerPage={queryParams.rowsPerPage}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          totalCount={totalCount}
          _order={[queryParams.sortOrder]}
          _orderBy={[queryParams.orderBy]}
        />

        <MikeSlidingPanel
          position="right"
          isOpen={createProjectOpen}
          onClose={onCreateProjectCloseClick}
          titleArea={intl.formatMessage({
            id: 'createProjectForm.heading.title'
          })}
          contentArea={
            <CreateProjectForm
              editing={false}
              initialValues={null}
              isFolder
              loadingCustomerUsers={loadingCustomerUsers}
              loadingProjectMembers={loadingProjectMembers}
              onSubmit={onCreateProject}
            />
          }
          actionsArea={null}
          noGrayOverlay={false}
        />

        <MikeSlidingPanel
          position="right"
          isOpen={editProjectOpen}
          onClose={() => {
            setEditProjectOpen(false)
            setEditingProject(null)
          }}
          titleArea={intl.formatMessage({
            id: 'createProjectForm.heading.label'
          })}
          contentArea={
            editableProjectWithMembers && (
              <CreateProjectForm
                editing={true}
                initialValues={editableProjectWithMembers}
                isFolder
                hideMemberField={true}
                loadingCustomerUsers={loadingCustomerUsers}
                loadingProjectMembers={loadingProjectMembers}
                onSubmit={onUpdateProject}
              />
            )
          }
          actionsArea={null}
          noGrayOverlay={false}
        />

        <FolderAccessDialog
          open={manageAccessPanel.open}
          projectId={manageAccessPanel.projectId}
          onClose={() => setManageAccessPanel({ open: false, projectId: null })}
        />
      </StyledTableTopHeader>
    </>
  )
}

export default RootProject
